import { useMemo, useState } from 'react';
import { latestMovies } from '@/mocks/movies';
import { hotSeries } from '@/mocks/series';
import SectionHeading from '@/pages/home/components/SectionHeading';

interface CatalogItem {
  id: string;
  title: string;
  kind: string;
  year: string;
  genres: string[];
  rating: number;
  poster: string;
}

const catalog: CatalogItem[] = [
  ...latestMovies.map((m) => ({
    id: m.id,
    title: m.title,
    kind: m.kind,
    year: m.year,
    genres: m.genres,
    rating: m.rating,
    poster: m.poster,
  })),
  ...hotSeries.map((s) => ({
    id: s.id,
    title: s.title,
    kind: s.kind,
    year: s.year,
    genres: s.genres,
    rating: s.rating,
    poster: s.poster,
  })),
];

const allGenres = Array.from(new Set(catalog.flatMap((item) => item.genres)));

export default function CategoryExplorer() {
  const [activeGenre, setActiveGenre] = useState('全部');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'default' | 'rating' | 'year'>('default');

  const results = useMemo(() => {
    const keyword = query.trim();
    let list = catalog.filter((item) => {
      const matchGenre = activeGenre === '全部' || item.genres.includes(activeGenre);
      const matchKeyword = !keyword || item.title.includes(keyword);
      return matchGenre && matchKeyword;
    });
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      list = [...list].sort((a, b) => b.year.localeCompare(a.year));
    }
    return list;
  }, [activeGenre, query, sortBy]);

  const resetFilters = () => {
    setActiveGenre('全部');
    setQuery('');
    setSortBy('default');
  };

  const hasFilter = activeGenre !== '全部' || query.trim() !== '' || sortBy !== 'default';

  return (
    <section id="category" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading
          tag="分类片库"
          title="按类型与关键字，快速找到想看的内容"
          desc="这里的筛选只作用于本页已收录的作品，选择类型、输入片名关键字或调整排序，结果会立刻更新，无需跳转页面。"
        />

        <div className="rounded-2xl bg-background-100/70 border border-background-200/70 p-4 md:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative w-full lg:max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索片名关键字"
                aria-label="搜索片名关键字"
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
              <label htmlFor="sort-select" className="text-sm text-foreground-600 whitespace-nowrap">
                排序
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'default' | 'rating' | 'year')}
                className="px-3 py-2.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              >
                <option value="default">默认排序</option>
                <option value="rating">按评分优先</option>
                <option value="year">按年份最新</option>
              </select>
              <button
                type="button"
                onClick={resetFilters}
                disabled={!hasFilter}
                className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                  hasFilter
                    ? 'bg-background-50 border border-background-300/70 text-foreground-800 hover:border-primary-300 hover:text-primary-600 cursor-pointer'
                    : 'bg-background-200/60 text-foreground-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-refresh-line"></i>
                重置
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {['全部', ...allGenres].map((genre) => {
              const active = activeGenre === genre;
              return (
                <button
                  key={genre}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveGenre(genre)}
                  className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 border border-background-300/60 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {genre}
                </button>
              );
            })}
          </div>

          <p className="text-xs md:text-sm text-foreground-500 mt-4">
            共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部作品
            {activeGenre !== '全部' && <span>　·　类型：{activeGenre}</span>}
            {query.trim() && <span>　·　关键字：{query.trim()}</span>}
          </p>
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 mt-8">
            {results.map((item) => (
              <article key={item.id} className="bg-background-100/70 rounded-xl overflow-hidden border border-background-200/70">
                <div className="relative w-full h-[190px] sm:h-[220px] lg:h-[230px]">
                  <img
                    src={item.poster}
                    alt={`${item.kind}《${item.title}》海报`}
                    title={`${item.title} ${item.genres.join(' ')} 在线观看`}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-foreground-950/70 text-background-50 text-[11px]">
                    {item.kind}
                  </span>
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-[11px] font-bold">
                    {item.rating.toFixed(1)}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 truncate">
                    {item.title}
                  </h3>
                  <p className="text-[11px] text-foreground-500 mt-1">
                    {item.year} · {item.genres.join(' / ')}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-dashed border-background-300/80 bg-background-100/50 py-14 text-center">
            <span className="inline-flex w-12 h-12 rounded-full bg-background-200/70 text-foreground-500 items-center justify-center">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-700 mt-3">没有找到符合条件的内容</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              清除筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}